import React, {
    useEffect,
    useMemo
} from 'react';

import {
    useDispatch,
    useSelector
} from 'react-redux';

import useConfigAuth
    from '../../../../Hooks/useConfigAuth.js';

import {

    cargarPaisesUbicacionAction,
    cargarConfigPaisUbicacionAction,
    cargarOpcionesUbicacionAction,
    crearClaveOpcionesUbicacion

} from '../../../../actions/ubicacionActions.js';

import SelectorUbicacionManual
    from './SelectorUbicacionManual.js';

import {

    ContenedorUbicacion,
    GridUbicacion,
    CampoUbicacion,
    LabelUbicacion,
    InputUbicacion,
    SelectUbicacion,
    TextoCargaUbicacion

} from '../../../Css/StyledUbicacion.js';


const SelectorUbicacion = ({

    value = {},

    onChange,

    disabled = false,

    prefijo = 'ubicacion',

    titulo = ''

}) => {

    const dispatch =
        useDispatch();

    const [config] =
        useConfigAuth();


    /*
    ========================================
    REDUX
    ========================================
    */

    const {

        paises,
        configPorPais,
        opciones,
        loadingPaises,
        loadingConfigPorPais,
        loadingOpciones

    } = useSelector(
        (state) =>
            state.ubicacion
    );


    /*
    ========================================
    DATOS ACTUALES
    ========================================

    El componente NO guarda la selección
    en Redux.

    Redux guarda solamente los catálogos.

    La selección concreta pertenece al
    formulario que está utilizando este
    selector.

    Eso permite tener simultáneamente:

    - ubicación paciente
    - ubicación Tutor 1
    - ubicación Tutor 2
    */

    const paisCodigo =
        value.pais_codigo ||
        '';


    const selecciones =
        value.niveles ||
        {};


    const configPais =
        paisCodigo
            ?
            configPorPais[
                paisCodigo
            ]
            :
            null;


    const niveles =
        configPais?.niveles ||
        [];


    /*
    ========================================
    PAÍSES ORDENADOS
    ========================================

    Orden alfabético.

    "Otro país" siempre queda último.
    */

    const paisesOrdenados =
        useMemo(
            () => {

                return [
                    ...paises
                ].sort(
                    (
                        a,
                        b
                    ) => {

                        /*
                        "Otro país" siempre primero.
                        */

                        if (
                            a.codigo ===
                            'OTRO'
                        ) {

                            return -1;
                        }


                        if (
                            b.codigo ===
                            'OTRO'
                        ) {

                            return 1;
                        }


                        /*
                        El resto de los países
                        en orden alfabético.
                        */

                        return a.nombre
                            .localeCompare(
                                b.nombre,
                                'es',
                                {
                                    sensitivity:
                                        'base'
                                }
                            );
                    }
                );

            },
            [
                paises
            ]
        );


    /*
    ========================================
    CARGAR PAÍSES
    ========================================
    */

    useEffect(
        () => {

            dispatch(
                cargarPaisesUbicacionAction({
                    config
                })
            );

        },
        [
            dispatch,
            config
        ]
    );


    /*
    ========================================
    CARGAR CONFIGURACIÓN PAÍS
    ========================================
    */

    useEffect(
        () => {

            if (
                !paisCodigo ||
                paisCodigo ===
                    'OTRO'
            ) {

                return;
            }


            const informacion = {

                config,

                payload: {

                    pais:
                        paisCodigo

                }

            };


            dispatch(
                cargarConfigPaisUbicacionAction(
                    informacion
                )
            );

        },
        [
            dispatch,
            config,
            paisCodigo
        ]
    );


    /*
    ========================================
    OBTENER SELECCIÓN DE NIVEL
    ========================================
    */

    const obtenerSeleccionNivel = (
        configNivel
    ) => {

        return selecciones[
            configNivel.key
        ] || null;
    };


    /*
    ========================================
    OBTENER PADRE DE NIVEL
    ========================================
    */

    const obtenerParentNivel = (
        indice
    ) => {

        if (
            indice === 0
        ) {

            return null;
        }


        const nivelAnterior =
            niveles[
                indice - 1
            ];


        const seleccionAnterior =
            obtenerSeleccionNivel(
                nivelAnterior
            );


        return (
            seleccionAnterior?.codigo ||
            null
        );
    };


    /*
    ========================================
    CARGAR OPCIONES
    ========================================

    Solamente se carga un nivel cuando
    su nivel padre ya fue seleccionado.

    Ejemplo Chile:

    Región
       ↓
    Provincia
       ↓
    Comuna
    */

    useEffect(
        () => {

            if (
                !paisCodigo ||
                paisCodigo ===
                    'OTRO' ||
                !configPais
            ) {

                return;
            }


            niveles.forEach(
                (
                    configNivel,
                    indice
                ) => {

                    let parent =
                        null;


                    if (
                        indice > 0
                    ) {

                        const nivelAnterior =
                            niveles[
                                indice - 1
                            ];


                        const seleccionAnterior =
                            selecciones[
                                nivelAnterior.key
                            ];


                        if (
                            !seleccionAnterior ||
                            !seleccionAnterior.codigo ||
                            seleccionAnterior.codigo ===
                                '__OTRA__'
                        ) {

                            return;
                        }


                        parent =
                            seleccionAnterior.codigo;
                    }


                    const informacion = {

                        config,

                        payload: {

                            pais:
                                paisCodigo,

                            nivel:
                                configNivel.nivel,

                            parent

                        }

                    };


                    dispatch(
                        cargarOpcionesUbicacionAction(
                            informacion
                        )
                    );
                }
            );

        },
        [
            dispatch,
            config,
            paisCodigo,
            configPais,
            niveles,
            selecciones
        ]
    );


    /*
    ========================================
    CAMBIAR PAÍS
    ========================================
    */

    const cambiarPais = (
        e
    ) => {

        const codigo =
            e.target.value;


        const paisSeleccionado =
            paises.find(
                (
                    pais
                ) =>
                    pais.codigo ===
                    codigo
            );


        onChange?.({

            pais_codigo:
                codigo,

            pais:
                codigo === 'OTRO'
                    ?
                    ''
                    :
                    (
                        paisSeleccionado
                            ?.nombre ||
                        ''
                    ),

            pais_manual:
                '',

            niveles:
                {},

            /*
            Estos campos se utilizan en
            la carga manual de "Otro país".
            */

            provincia:
                '',

            provincia_id:
                '',

            departamento:
                '',

            departamento_id:
                '',

            localidad:
                '',

            localidad_id:
                '',

            localidad_manual:
                ''

        });
    };


    /*
    ========================================
    OPCIONES DE UN NIVEL
    ========================================
    */

    const obtenerOpcionesNivel = (
        configNivel,
        indice
    ) => {

        const parent =
            obtenerParentNivel(
                indice
            );


        const clave =
            crearClaveOpcionesUbicacion(

                paisCodigo,

                configNivel.nivel,

                parent

            );


        return {

            clave,

            informacion:
                opciones[
                    clave
                ] || [],

            loading:
                Boolean(
                    loadingOpciones[
                        clave
                    ]
                )

        };
    };


    /*
    ========================================
    CAMBIAR NIVEL
    ========================================
    */

    const cambiarNivel = (
        configNivel,
        indice,
        codigo
    ) => {

        const {
            informacion
        } =
            obtenerOpcionesNivel(
                configNivel,
                indice
            );


        /*
        ----------------------------------------
        CREAR NUEVO OBJETO DE NIVELES
        ----------------------------------------

        Conservamos solamente niveles
        anteriores.

        Si cambia Región, se borran
        Provincia y Comuna.

        Si cambia Provincia, se borra
        Comuna.
        */

        const nuevosNiveles =
            {};


        niveles.forEach(
            (
                nivelItem,
                indiceItem
            ) => {

                if (
                    indiceItem <
                    indice &&
                    selecciones[
                        nivelItem.key
                    ]
                ) {

                    nuevosNiveles[
                        nivelItem.key
                    ] =
                        selecciones[
                            nivelItem.key
                        ];
                }
            }
        );


        /*
        ========================================
        NO ENCUENTRO MI LOCALIDAD
        ========================================
        */

        if (
            codigo ===
            '__OTRA__'
        ) {

            nuevosNiveles[
                configNivel.key
            ] = {

                codigo:
                    '__OTRA__',

                nombre:
                    '',

                manual:
                    true

            };


            onChange?.({

                ...value,

                niveles:
                    nuevosNiveles,

                localidad_manual:
                    '',

                localidad:
                    '',

                localidad_id:
                    ''

            });


            return;
        }


        /*
        ========================================
        SELECCIÓN VACÍA
        ========================================
        */

        if (
            !codigo
        ) {

            onChange?.({

                ...value,

                niveles:
                    nuevosNiveles,

                localidad_manual:
                    ''

            });


            return;
        }


        /*
        ========================================
        SELECCIÓN NORMAL
        ========================================
        */

        const opcionSeleccionada =
            informacion.find(
                (
                    opcion
                ) =>
                    String(
                        opcion.codigo
                    ) ===
                    String(
                        codigo
                    )
            );


        if (
            opcionSeleccionada
        ) {

            nuevosNiveles[
                configNivel.key
            ] = {

                nivel:
                    configNivel.nivel,

                key:
                    configNivel.key,

                tipo:
                    configNivel.etiqueta,

                es_localidad:
                    Boolean(
                        configNivel.es_localidad
                    ),

                codigo:
                    String(
                        opcionSeleccionada.codigo
                    ),

                nombre:
                    opcionSeleccionada.nombre,

                latitud:
                    opcionSeleccionada
                        .latitud ??
                    null,

                longitud:
                    opcionSeleccionada
                        .longitud ??
                    null,

                metadata:
                    opcionSeleccionada
                        .metadata ||
                    {}

            };
        }


        onChange?.({

            ...value,

            niveles:
                nuevosNiveles,

            localidad_manual:
                ''

        });
    };


    /*
    ========================================
    PUEDE MOSTRAR NIVEL
    ========================================
    */

    const puedeMostrarNivel = (
        indice
    ) => {

        if (
            indice === 0
        ) {

            return true;
        }


        const nivelAnterior =
            niveles[
                indice - 1
            ];


        const seleccionAnterior =
            selecciones[
                nivelAnterior.key
            ];


        return Boolean(

            seleccionAnterior?.codigo &&

            seleccionAnterior.codigo !==
                '__OTRA__'

        );
    };


    /*
    ========================================
    CAMBIAR PAÍS MANUAL
    ========================================
    */

    const cambiarPaisManual = (
        e
    ) => {

        const paisManual =
            e.target.value;


        onChange?.({

            ...value,

            pais_manual:
                paisManual,

            pais:
                paisManual

        });
    };


    /*
    ========================================
    CAMBIO UBICACIÓN MANUAL
    ========================================
    */

    const cambiarUbicacionManual = (
        nuevaUbicacion
    ) => {

        onChange?.({

            ...value,

            ...nuevaUbicacion

        });
    };


    /*
    ========================================
    CAMBIAR LOCALIDAD MANUAL
    ========================================
    */

    const cambiarLocalidadManual = (
        e
    ) => {

        const localidad =
            e.target.value;


        onChange?.({

            ...value,

            localidad_manual:
                localidad,

            localidad:
                localidad,

            localidad_id:
                ''

        });
    };


    /*
    ========================================
    ETIQUETA VISUAL DEL NIVEL
    ========================================

    En CABA mostramos nombres más claros:

    Nivel 2:
    Comuna

    Nivel 3:
    Barrio
    */

    const obtenerEtiquetaNivel = (
        configNivel
    ) => {

        const provinciaSeleccionada =
            selecciones.nivel_1;


        const esCaba =

            paisCodigo === 'AR' &&

            provinciaSeleccionada
                ?.codigo === '02';


        if (
            esCaba &&
            configNivel.nivel === 2
        ) {

            return 'Comuna';

        }


        if (
            esCaba &&
            configNivel.nivel === 3
        ) {

            return 'Barrio';

        }


        return configNivel.etiqueta;

    };


    return (

        <ContenedorUbicacion>

            {titulo && (

                <div>
                    {titulo}
                </div>

            )}


            <GridUbicacion>


                {/* ==================================
                    PAÍS
                =================================== */}

                <CampoUbicacion>

                    <LabelUbicacion
                        htmlFor={
                            `${prefijo}-pais`
                        }
                    >
                        País
                    </LabelUbicacion>


                    <SelectUbicacion

                        id={
                            `${prefijo}-pais`
                        }

                        value={
                            paisCodigo
                        }

                        disabled={
                            disabled ||
                            loadingPaises
                        }

                        onChange={
                            cambiarPais
                        }

                    >

                        <option value="">

                            {loadingPaises
                                ?
                                'Cargando países...'
                                :
                                'Seleccionar país'
                            }

                        </option>


                        {paisesOrdenados.map(
                            (
                                pais
                            ) => (

                                <option

                                    key={
                                        pais.codigo
                                    }

                                    value={
                                        pais.codigo
                                    }

                                >

                                    {pais.codigo ===
                                        'OTRO'
                                        ?
                                        'Otro país'
                                        :
                                        pais.nombre
                                    }

                                </option>

                            )
                        )}

                    </SelectUbicacion>


                    {loadingPaises && (

                        <TextoCargaUbicacion>
                            Cargando países...
                        </TextoCargaUbicacion>

                    )}

                </CampoUbicacion>


                {/* ==================================
                    OTRO PAÍS - NOMBRE
                =================================== */}

                {paisCodigo ===
                    'OTRO' && (

                    <CampoUbicacion>

                        <LabelUbicacion
                            htmlFor={
                                `${prefijo}-pais-manual`
                            }
                        >
                            País
                        </LabelUbicacion>


                        <InputUbicacion

                            id={
                                `${prefijo}-pais-manual`
                            }

                            type="text"

                            value={
                                value.pais_manual ||
                                ''
                            }

                            disabled={
                                disabled
                            }

                            placeholder="Escribí el país"

                            onChange={
                                cambiarPaisManual
                            }

                        />

                    </CampoUbicacion>

                )}


                {/* ==================================
                    CONFIGURACIÓN CARGANDO
                =================================== */}

                {paisCodigo &&
                    paisCodigo !== 'OTRO' &&
                    !configPais &&
                    loadingConfigPorPais[
                        paisCodigo
                    ] && (

                    <CampoUbicacion>

                        <TextoCargaUbicacion>
                            Cargando ubicación...
                        </TextoCargaUbicacion>

                    </CampoUbicacion>

                )}


                {/* ==================================
                    NIVELES CONFIGURADOS
                =================================== */}

                {paisCodigo &&
                    paisCodigo !== 'OTRO' &&
                    configPais &&
                    niveles.map(
                        (
                            configNivel,
                            indice
                        ) => {

                            if (
                                !puedeMostrarNivel(
                                    indice
                                )
                            ) {

                                return null;
                            }


                            const {

                                informacion,
                                loading

                            } =
                                obtenerOpcionesNivel(
                                    configNivel,
                                    indice
                                );


                            const seleccion =
                                obtenerSeleccionNivel(
                                    configNivel
                                );


                            const codigoSeleccionado =
                                seleccion?.codigo ||
                                '';


                            return (

                                <CampoUbicacion

                                    key={
                                        `${paisCodigo}-${configNivel.key}`
                                    }

                                >

                                    <LabelUbicacion

                                        htmlFor={
                                            `${prefijo}-${configNivel.key}`
                                        }

                                    >

                                        {
                                            obtenerEtiquetaNivel(
                                                configNivel
                                            )
                                        }

                                    </LabelUbicacion>


                                    <SelectUbicacion

                                        id={
                                            `${prefijo}-${configNivel.key}`
                                        }

                                        value={
                                            codigoSeleccionado
                                        }

                                        disabled={
                                            disabled ||
                                            loading
                                        }

                                        onChange={
                                            (
                                                e
                                            ) =>
                                                cambiarNivel(

                                                    configNivel,

                                                    indice,

                                                    e.target.value

                                                )
                                        }

                                    >

                                        <option value="">

                                            {loading
                                                ?
                                                'Cargando...'
                                                :
                                                'Seleccionar'
                                            }

                                        </option>

                                        {configNivel
                                            .es_localidad && (

                                            <option
                                                value="__OTRA__"
                                            >
                                                No encuentro mi localidad
                                            </option>

                                        )}

                                        {informacion.map(
                                            (
                                                opcion
                                            ) => (

                                                <option

                                                    key={
                                                        opcion.codigo
                                                    }

                                                    value={
                                                        opcion.codigo
                                                    }

                                                >

                                                    {
                                                        opcion.metadata
                                                            ?.nombre_mostrar ||
                                                        opcion.nombre
                                                    }

                                                </option>

                                            )
                                        )}


                                    </SelectUbicacion>


                                    {loading && (

                                        <TextoCargaUbicacion>
                                            Cargando...
                                        </TextoCargaUbicacion>

                                    )}

                                </CampoUbicacion>

                            );
                        }
                    )
                }


                {/* ==================================
                    LOCALIDAD MANUAL
                =================================== */}

                {paisCodigo !==
                    'OTRO' &&
                    niveles.some(
                        (
                            nivel
                        ) =>

                            nivel.es_localidad &&

                            selecciones[
                                nivel.key
                            ]?.codigo ===
                                '__OTRA__'
                    ) && (

                    <CampoUbicacion>

                        <LabelUbicacion
                            htmlFor={
                                `${prefijo}-localidad-manual`
                            }
                        >
                            Otra localidad
                        </LabelUbicacion>


                        <InputUbicacion

                            id={
                                `${prefijo}-localidad-manual`
                            }

                            type="text"

                            value={
                                value.localidad_manual ||
                                ''
                            }

                            disabled={
                                disabled
                            }

                            placeholder="Escribí la localidad"

                            onChange={
                                cambiarLocalidadManual
                            }

                        />

                    </CampoUbicacion>

                )}

            </GridUbicacion>


            {/* ==================================
                OTRO PAÍS
            =================================== */}

            {paisCodigo ===
                'OTRO' && (

                <SelectorUbicacionManual

                    value={
                        value
                    }

                    onChange={
                        cambiarUbicacionManual
                    }

                    disabled={
                        disabled
                    }

                    prefijo={
                        `${prefijo}-manual`
                    }

                />

            )}

        </ContenedorUbicacion>

    );
};


export default SelectorUbicacion;